import axios from 'axios';
import http_method from './http_method';
import { useContentStore } from '../stores/content_store';
import Content from '../classes/content.js';
import { useCaretStore } from '../stores/caret_store.js';
import caret_method from './caret_method';
import { nextTick } from 'vue';
import { graphemeCount, sliceGraphemes } from '../text_segment.js';

export default {
  mixins: [http_method, caret_method],
  computed: {
    contents() {
      return useContentStore().contents;
    },
    /**
     * UUID → Content の Map（O(1)検索用）
     * @returns {Map<string, Content>}
     */
    contentMap() {
      const map = new Map();
      this.contents.forEach((c) => map.set(c.uuid, c));
      return map;
    },
  },
  methods: {
    /**
     * sentenceを取得
     * @param {} null
     *
     */
    async getSentences() {
      const response = await this.getSelect(
        {},
        `/documents/${this.documentId}/get_sentences.json`,
        'テキストの取得に失敗しました。',
      );
      if (response) {
        let previousContent = null;
        useContentStore().contents = response.data.sentences
          .sort((a, b) => {
            return a.sort_key - b.sort_key;
          })
          .map((contentData) => {
            const contentObject = new Content(contentData);
            // DBはCU基準。in-memoryは書記素基準なのでロード直後に変換する。
            contentObject.convertStyleFromCodeUnit();
            contentObject.previous = previousContent;
            if (previousContent) previousContent.next = contentObject;
            previousContent = contentObject;
            return contentObject;
          });
      }
      if (this.contents.length == 0) {
        this.contents.push(new Content({ tag_type: 'togaki' }));
      }
      nextTick(() => {
        const selection = window.getSelection();
        const range = document.createRange();
        let nextFocusContentElements = document.querySelectorAll(`[data-contentid="${this.contents[0].uuid}"]`);
        let nextFocusDiv = null;
        nextFocusContentElements.forEach((ele) => {
          let selectorArray = ele.querySelectorAll(`[data-index="0"]`);
          if (selectorArray.length == 1) nextFocusDiv = selectorArray[0];
        });
        range.setStart(nextFocusDiv, 0);
        range.setEnd(nextFocusDiv, 0);
        selection.removeAllRanges();
        selection.addRange(range);
      });
      this.isGettingSentences = false;
    },
    /**
     * すべてのcontentのstatusをsavedにする
     * ただし、editingのものは変更しない
     * @param {Boolean} isInit 読み込み時の作成かどうか
     *
     */
    setStatusSaved(isInit = false) {
      if (isInit) useContentStore().contents.forEach((content) => (content.status = 'saved'));
      else
        useContentStore()
          .contents.filter((content) => content.status == 'saving')
          .forEach((content) => {
            content.status = 'saved';
          });
    },
    /**
     * sort_keyからcontentを取得
     * @param {Number} sort_key
     *
     */
    getContentBySortKey(sort_key) {
      return this.contents.find((content) => content.sort_key == sort_key);
    },
    /**
     * Documentの保存処理
     * @param {Array<Object>} key_params
     * @param {Array<Object>} sentence_params
     *
     */
    async updateDocument() {
      this.saving = true;
      let sortKeyCount = 0;
      let formData = new FormData();
      formData.append(`document[title]`, this.title);
      let currentContent = this.contents[0];
      while (currentContent) {
        formData.append(`document[sentences_attributes][][body]`, currentContent.body);
        formData.append(`document[sentences_attributes][][tag_type]`, currentContent.tag_type);
        formData.append(`document[sentences_attributes][][sort_key]`, sortKeyCount);
        sortKeyCount++;
        // in-memory の style は書記素基準。DB へはコードユニット基準に変換して保存する。
        currentContent.styleAsCodeUnit().forEach((style) => {
          formData.append(`document[sentences_attributes][][styles_attributes][][style_type]`, style.style_type);
          formData.append(`document[sentences_attributes][][styles_attributes][][color]`, style.color);
          formData.append(`document[sentences_attributes][][styles_attributes][][start_index]`, style.start_index);
          formData.append(`document[sentences_attributes][][styles_attributes][][end_index]`, style.end_index);
        });
        currentContent = currentContent.next;
      }
      const response = await this.patchMethod(formData, `/documents/${this.documentId}.json`, '保存に失敗しました。');
      this.closeMessages();
      this.saving = false;
      if (response && response.status == 200) {
        this.setStatusSaved();
        return response;
      } else {
        this.announceMessage('error', '保存に失敗しました。');
        return false;
      }
    },
    /**
     * 選択範囲の変更
     *
     */
    updateSelectRange() {
      const fromContent = this.contentMap.get(this.selectFrom.contentID);
      let toContent = null;
      let currentContent = fromContent;
      let isNext = false;
      while (!toContent && currentContent) {
        if (currentContent.uuid == this.selectTo.contentID) {
          toContent = currentContent;
          isNext = true;
        }
        currentContent = currentContent.next;
      }
      currentContent = fromContent;
      while (!toContent && currentContent) {
        if (currentContent.uuid == this.selectTo.contentID) {
          toContent = currentContent;
        }
        currentContent = currentContent.previous;
      }
      if (toContent) {
        this.contents.forEach((content) => {
          if (content.selectFrom != -1) {
            content.selectFrom = -1;
          }
          if (content.selectTo != -1) {
            content.selectTo = -1;
          }
        });
        if (fromContent == toContent) {
          fromContent.selectFrom =
            this.selectFrom.wordIndex > this.selectTo.wordIndex ? this.selectTo.wordIndex : this.selectFrom.wordIndex;
          fromContent.selectTo =
            this.selectFrom.wordIndex > this.selectTo.wordIndex ? this.selectFrom.wordIndex : this.selectTo.wordIndex;
        } else {
          currentContent = fromContent;
          fromContent.selectFrom = isNext ? this.selectFrom.wordIndex : 0;
          fromContent.selectTo = isNext ? currentContent.bodyGraphemeLength - 1 : this.selectFrom.wordIndex;
          while (true) {
            currentContent = isNext ? currentContent.next : currentContent.previous;
            if (currentContent.uuid != this.selectTo.contentID) {
              currentContent.selectFrom = 0;
              currentContent.selectTo = currentContent.bodyGraphemeLength - 1;
            } else {
              currentContent.selectFrom = isNext ? 0 : this.selectTo.wordIndex;
              currentContent.selectTo = isNext ? this.selectTo.wordIndex : currentContent.bodyGraphemeLength - 1;
              break;
            }
          }
        }
      }
    },
    /**
     * 編集したコンテンツの検索情報を再計算する
     * DocumentEditorは自身のreSearchEditedContentを呼び、子コンポーネント(BasicSentence/Hashira等)は
     * reSearchEditedContentを持たないため親へsearch-refreshをemitして委譲する
     * @param {Content} content
     */
    refreshSearchForContent(content) {
      if (!content) return;
      if (typeof this.reSearchEditedContent === 'function') {
        this.reSearchEditedContent(content);
      } else {
        this.$emit('search-refresh', content);
      }
    },
    /**
     * 範囲選択中選択範囲全削除の処理
     */
    async deleteSelectedWordsAndSetCaret(options = {}) {
      const fromContent = this.contentMap.get(this.selectFrom.contentID);
      let toContent = null;
      let currentContent = fromContent;
      let isNext = false;
      while (!toContent && currentContent) {
        if (currentContent.uuid == this.selectTo.contentID) {
          toContent = currentContent;
          isNext = true;
        }
        currentContent = currentContent.next;
      }
      currentContent = fromContent;
      while (!toContent && currentContent) {
        if (currentContent.uuid == this.selectTo.contentID) {
          toContent = currentContent;
        }
        currentContent = currentContent.previous;
      }
      if (!toContent) return;
      if (fromContent == toContent) {
        const deleteStart = Math.min(this.selectFrom.wordIndex, this.selectTo.wordIndex);
        const deleteEnd = Math.max(this.selectFrom.wordIndex, this.selectTo.wordIndex);
        const deletedCount = deleteEnd - deleteStart + 1;

        // テキストを削除
        fromContent.updateBody(
          sliceGraphemes(fromContent.updatingbody, 0, deleteStart) +
            sliceGraphemes(fromContent.updatingbody, deleteEnd + 1),
        );

        // スタイル調整
        // 1. 選択範囲の開始位置にかかるスタイル
        let inRangeFromStyles = fromContent.style.filter(
          (style) =>
            style.start_index <= style.end_index &&
            style.start_index <= deleteStart &&
            deleteStart <= style.end_index + 1,
        );
        // 2. 選択範囲の終了位置にかかるスタイル
        let inRangeToStyles = fromContent.style.filter(
          (style) =>
            style.start_index <= style.end_index && style.start_index <= deleteEnd + 1 && deleteEnd <= style.end_index,
        );
        // 3. 選択範囲より前または後ろにあるスタイル（選択範囲と重ならない）
        fromContent.style = fromContent.style.filter(
          (style) =>
            style.start_index <= style.end_index &&
            (deleteStart > style.end_index + 1 || style.start_index > deleteEnd + 1),
        );

        // 選択範囲の前後にまたがるスタイルを結合
        inRangeFromStyles.forEach((style) => {
          let joinStyleIndex = inRangeToStyles.findIndex(
            (s) => style.style_type == s.style_type && style.color == s.color,
          );
          if (joinStyleIndex >= 0) {
            // 同じスタイルがある場合は結合
            fromContent.style.push({
              style_type: style.style_type,
              color: style.color,
              start_index: style.start_index,
              end_index: inRangeToStyles[joinStyleIndex].end_index - deletedCount,
            });
            inRangeToStyles.splice(joinStyleIndex, 1);
          } else {
            // 選択範囲の開始位置までのスタイルを保持
            fromContent.style.push({
              style_type: style.style_type,
              color: style.color,
              start_index: style.start_index,
              end_index: deleteStart - 1,
            });
          }
        });
        // 選択範囲の終了位置からのスタイルを調整
        inRangeToStyles.forEach((style) => {
          fromContent.style.push({
            style_type: style.style_type,
            color: style.color,
            start_index: deleteStart,
            end_index: style.end_index - deletedCount,
          });
        });

        // 選択範囲より後ろのスタイルのインデックスを調整
        fromContent.style.forEach((style) => {
          if (style.start_index > deleteEnd) {
            style.start_index -= deletedCount;
            style.end_index -= deletedCount;
          }
        });
      } else {
        currentContent = fromContent;
        const deletedUUIDs = []; // ストアから除去するUUIDを収集
        if (isNext) {
          // fromContentのselectFrom以降を削除
          fromContent.updateBody(sliceGraphemes(fromContent.updatingbody, 0, this.selectFrom.wordIndex));
          // fromContentのselectFrom以降のスタイルを削除、selectFromにかかるスタイルは終了位置を調整
          fromContent.style = fromContent.style
            .filter((style) => style.start_index < this.selectFrom.wordIndex)
            .map((style) => {
              if (style.end_index >= this.selectFrom.wordIndex) {
                return {
                  ...style,
                  end_index: this.selectFrom.wordIndex - 1,
                };
              }
              return style;
            });
        } else {
          // fromContentの0からselectFromまでを削除
          const deletedCount = this.selectFrom.wordIndex + 1;
          fromContent.updateBody(sliceGraphemes(fromContent.updatingbody, this.selectFrom.wordIndex + 1));
          // fromContentのselectFrom以前のスタイルを削除、selectFromより後のスタイルのインデックスを調整
          fromContent.style = fromContent.style
            .filter((style) => style.end_index > this.selectFrom.wordIndex)
            .map((style) => ({
              ...style,
              start_index: Math.max(0, style.start_index - deletedCount),
              end_index: style.end_index - deletedCount,
            }));
        }
        currentContent = isNext ? currentContent.next : currentContent.previous;
        while (true) {
          if (currentContent.uuid != this.selectTo.contentID) {
            currentContent.previous.next = currentContent.next;
            currentContent.next.previous = currentContent.previous;
            let deleteContent = currentContent;
            currentContent = isNext ? currentContent.next : currentContent.previous;
            deletedUUIDs.push(deleteContent.uuid); // 削除UUIDを収集
            deleteContent.previous = null;
            deleteContent.next = null;
          } else {
            if (isNext) {
              // toContentの0からselectToまでを削除して、fromContentに結合
              const remainingText = sliceGraphemes(currentContent.updatingbody, this.selectTo.wordIndex + 1);
              const fromContentLength = fromContent.graphemeLength;
              fromContent.updateBody(fromContent.updatingbody + remainingText);

              // fromContentのselectFrom-1の位置（結合点の直前）にかかるスタイルを探す
              const fromContentEndStyles = fromContent.style.filter(
                (style) =>
                  style.start_index <= this.selectFrom.wordIndex - 1 &&
                  style.end_index >= this.selectFrom.wordIndex - 1,
              );

              // toContentのselectTo+1の位置（結合点の直後）にかかるスタイルを探す
              const toContentStartStyles = currentContent.style.filter(
                (style) =>
                  style.start_index <= this.selectTo.wordIndex + 1 && style.end_index > this.selectTo.wordIndex,
              );

              // toContentのselectToより後のスタイルをfromContentに追加（インデックスを調整）
              const toContentStyles = currentContent.style
                .filter((style) => style.start_index > this.selectTo.wordIndex)
                .map((style) => ({
                  ...style,
                  start_index: fromContentLength + style.start_index - (this.selectTo.wordIndex + 1),
                  end_index: fromContentLength + style.end_index - (this.selectTo.wordIndex + 1),
                }));

              // toContentのselectToにかかるスタイルも考慮
              const crossingStyles = currentContent.style
                .filter(
                  (style) => style.start_index <= this.selectTo.wordIndex && style.end_index > this.selectTo.wordIndex,
                )
                .map((style) => ({
                  ...style,
                  start_index: fromContentLength,
                  end_index: fromContentLength + style.end_index - (this.selectTo.wordIndex + 1),
                }));

              // 結合点で同じスタイルがある場合は結合
              const mergedStyles = [];
              fromContentEndStyles.forEach((fromStyle) => {
                const matchingCrossingStyle = crossingStyles.find(
                  (crossingStyle) =>
                    crossingStyle.style_type === fromStyle.style_type &&
                    crossingStyle.color === fromStyle.color &&
                    crossingStyle.start_index === fromContentLength,
                );
                if (matchingCrossingStyle) {
                  // 同じスタイルが隣接している場合は結合
                  // fromContentのスタイルを延長
                  const fromStyleIndex = fromContent.style.findIndex(
                    (s) =>
                      s.style_type === fromStyle.style_type &&
                      s.color === fromStyle.color &&
                      s.end_index === this.selectFrom.wordIndex - 1,
                  );
                  if (fromStyleIndex >= 0) {
                    fromContent.style[fromStyleIndex].end_index = matchingCrossingStyle.end_index;
                    mergedStyles.push(matchingCrossingStyle);
                  }
                }
              });

              // 結合されなかったスタイルを追加
              const remainingCrossingStyles = crossingStyles.filter(
                (crossingStyle) =>
                  !mergedStyles.some(
                    (merged) =>
                      crossingStyle.style_type === merged.style_type &&
                      crossingStyle.color === merged.color &&
                      crossingStyle.start_index === merged.start_index,
                  ),
              );

              fromContent.style = [...fromContent.style, ...remainingCrossingStyles, ...toContentStyles];

              deletedUUIDs.push(currentContent.uuid); // マージ元をストアから除去
              fromContent.next = currentContent.next;
              currentContent.next = null;
              if (fromContent.next) fromContent.next.previous = fromContent;
              currentContent.previous = null;
            } else {
              // toContentのselectToより後を削除して、fromContentの残りをtoContentに結合
              const toContentRemaining = sliceGraphemes(currentContent.updatingbody, 0, this.selectTo.wordIndex);
              const toContentLength = graphemeCount(toContentRemaining);
              currentContent.updateBody(toContentRemaining + fromContent.updatingbody);

              // toContentのselectTo-1の位置（結合点の直前）にかかるスタイルを探す
              const toContentEndStyles = currentContent.style.filter(
                (style) =>
                  style.start_index <= this.selectTo.wordIndex - 1 && style.end_index >= this.selectTo.wordIndex - 1,
              );

              // fromContentの0の位置（結合点の直後）にかかるスタイルを探す
              const fromContentStartStyles = fromContent.style.filter(
                (style) => style.start_index <= 0 && style.end_index >= 0,
              );

              // toContentのselectTo以前のスタイルを保持、selectToにかかるスタイルは終了位置を調整
              currentContent.style = currentContent.style
                .filter((style) => style.start_index < this.selectTo.wordIndex)
                .map((style) => {
                  if (style.end_index >= this.selectTo.wordIndex) {
                    return {
                      ...style,
                      end_index: this.selectTo.wordIndex - 1,
                    };
                  }
                  return style;
                });

              // fromContentのスタイルをインデックス調整
              const fromContentStyles = fromContent.style.map((style) => ({
                ...style,
                start_index: toContentLength + style.start_index,
                end_index: toContentLength + style.end_index,
              }));

              // 結合点で同じスタイルがある場合は結合
              const mergedStyles = [];
              toContentEndStyles.forEach((toStyle) => {
                const matchingFromStyle = fromContentStartStyles.find(
                  (fromStyle) => fromStyle.style_type === toStyle.style_type && fromStyle.color === toStyle.color,
                );
                if (matchingFromStyle) {
                  // 同じスタイルが隣接している場合は結合
                  // toContentのスタイルを延長
                  const toStyleIndex = currentContent.style.findIndex(
                    (s) =>
                      s.style_type === toStyle.style_type &&
                      s.color === toStyle.color &&
                      s.end_index === this.selectTo.wordIndex - 1,
                  );
                  if (toStyleIndex >= 0) {
                    // fromContentの対応するスタイルのend_indexを取得
                    const adjustedEndIndex = toContentLength + matchingFromStyle.end_index;
                    currentContent.style[toStyleIndex].end_index = adjustedEndIndex;
                    mergedStyles.push(matchingFromStyle);
                  }
                }
              });

              // 結合されなかったfromContentのスタイルを追加
              const remainingFromStyles = fromContentStyles.filter(
                (fromStyle) =>
                  !mergedStyles.some(
                    (merged) =>
                      fromStyle.style_type === merged.style_type &&
                      fromStyle.color === merged.color &&
                      fromStyle.start_index === toContentLength + merged.start_index,
                  ),
              );

              currentContent.style = [...currentContent.style, ...remainingFromStyles];

              deletedUUIDs.push(fromContent.uuid); // マージ元をストアから除去
              currentContent.next = fromContent.next;
              fromContent.next = null;
              if (currentContent.next) currentContent.next.previous = currentContent;
              fromContent.previous = null;
            }
            break;
          }
        }
        // リンクリストから切り離されたコンテンツをPiniaストアからも除去
        if (deletedUUIDs.length > 0) {
          const store = useContentStore();
          store.contents = store.contents.filter((c) => !deletedUUIDs.includes(c.uuid));
        }
      }
      this.contents.forEach((content) => {
        if (content.selectFrom != -1) {
          content.selectFrom = -1;
        }
        if (content.selectTo != -1) {
          content.selectTo = -1;
        }
      });
      // 削除後に残る（結合先の）コンテンツ
      const survivingContent = isNext ? fromContent : toContent;
      // 編集で残ったコンテンツの検索情報を再計算する
      this.refreshSearchForContent(survivingContent);
      // skipCaret: trueの場合、nextTickでのカーソル再設定をスキップする
      // （Windows IMEではcompositionstart中にselectionを変更するとcompositionendが発火するため）
      if (options.skipCaret) {
        this.selectTo.contentID = '';
        this.selectTo.wordIndex = 0;
        return survivingContent;
      }
      await nextTick(() => {
        const selection = window.getSelection(); // caret位置取得用
        const range = document.createRange();
        let nextFocusContentElements = document.querySelectorAll(
          `[data-contentid="${isNext ? this.selectFrom.contentID : this.selectTo.contentID}"]`,
        );
        let nextFocusDiv = null;
        let focusIndex =
          this.selectFrom.contentID == this.selectTo.contentID
            ? Math.min(this.selectFrom.wordIndex, this.selectTo.wordIndex)
            : isNext
            ? this.selectFrom.wordIndex
            : this.selectTo.wordIndex;
        nextFocusContentElements.forEach((ele) => {
          let selectorArray = ele.querySelectorAll(`[data-index="${Math.max(0, focusIndex - 1)}"]`);
          if (selectorArray.length == 1) nextFocusDiv = selectorArray[0];
        });
        range.setStart(nextFocusDiv, focusIndex > 0 ? 1 : 0);
        range.setEnd(nextFocusDiv, focusIndex > 0 ? 1 : 0);
        selection.removeAllRanges();
        selection.addRange(range);
        this.selectTo.contentID = '';
        this.selectTo.wordIndex = 0;
      });
      return survivingContent;
    },
  },
};
